/**
 * Authentication slice - UPDATED TO USE DATABASE-BASED JOINED EVENTS
 * No more localStorage for joined events - all stored in database!
 */

import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { AuthUser } from '../../types/auth';
import { 
  joinEventInDatabase, 
  leaveEventInDatabase, 
  getUserJoinedEventsFromDatabase 
} from '../../services/apiService';



interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  joinedEvents: string[]; // Event IDs user has joined (loaded from database)
  loading: boolean; // For async join/leave operations
  initialized: boolean; // Has auth restoration/login state been resolved?
  error: string | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  joinedEvents: [], // No localStorage - loaded from database
  loading: false,
  error: null,
  initialized: false
};

// =============================================================================
// 🆕 ASYNC THUNKS FOR DATABASE-BASED JOINED EVENTS
// =============================================================================

/**
 * Join an event - Database-based
 */
export const joinEventAsync = createAsyncThunk(
  'auth/joinEvent',
  async ({ eventId, userId }: { eventId: string; userId: string }, { rejectWithValue }) => {
    try {
      console.log(`🎯 ASYNC JOIN: User ${userId} joining event ${eventId} via database...`);
      
      const result = await joinEventInDatabase(eventId, userId);
      
      if (result.success) {
        console.log(`✅ Database join successful for event ${eventId}`);
        return { eventId, message: result.message };
      } else {
        console.error(`❌ Database join failed: ${result.error}`);
        return rejectWithValue(result.error || 'Failed to join event');
      }
    } catch (error) {
      console.error('❌ Error in joinEventAsync:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

/**
 * Leave an event - Database-based
 */
export const leaveEventAsync = createAsyncThunk(
  'auth/leaveEvent',
  async ({ eventId, userId }: { eventId: string; userId: string }, { rejectWithValue }) => {
    try {
      console.log(`🎯 ASYNC LEAVE: User ${userId} leaving event ${eventId} via database...`);
      
      const result = await leaveEventInDatabase(eventId, userId);
      
      if (result.success) {
        console.log(`✅ Database leave successful for event ${eventId}`);
        return { eventId, message: result.message };
      } else {
        console.error(`❌ Database leave failed: ${result.error}`);
        return rejectWithValue(result.error || 'Failed to leave event');
      }
    } catch (error) {
      console.error('❌ Error in leaveEventAsync:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

/**
 * Load user's joined events from database
 */
export const loadJoinedEventsAsync = createAsyncThunk(
  'auth/loadJoinedEvents',
  async (userId: string, { rejectWithValue }) => {
    try {
      console.log(`📋 LOADING JOINED EVENTS: Getting events for user ${userId} from database...`);
      
      const result = await getUserJoinedEventsFromDatabase(userId);
      
      if (result.success) {
        console.log(`✅ Loaded ${result.joinedEventIds.length} joined events from database:`, result.joinedEventIds);
        return result.joinedEventIds;
      } else {
        console.error(`❌ Failed to load joined events: ${result.error}`);
        return rejectWithValue(result.error || 'Failed to load joined events');
      }
    } catch (error) {
      console.error('❌ Error in loadJoinedEventsAsync:', error);
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  }
);

// =============================================================================
// AUTH SLICE
// =============================================================================

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Login action - also loads joined events
    login: (state, action: PayloadAction<{ user: AuthUser; token: string }>) => {
      console.log('🔐 LOGIN ACTION:', action.payload.user.firstName);
      
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      
      // Save token and user to localStorage for persistence
      localStorage.setItem('crowdtrades_token', action.payload.token);
      localStorage.setItem('crowdtrades_user', JSON.stringify(action.payload.user));
      
      // Clear joined events - will be loaded from database
      state.joinedEvents = [];
      state.initialized = true;
      
      console.log(`✅ User logged in: ${action.payload.user.firstName} (${action.payload.user.id})`);
      console.log('📋 Joined events will be loaded from database');
    },

    // Logout action
    logout: (state) => {
      console.log('👋 LOGOUT ACTION');
      
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.joinedEvents = [];
      state.error = null;
      state.initialized = true;

      // Clear localStorage
      localStorage.removeItem('crowdtrades_token');
      localStorage.removeItem('crowdtrades_user');
      
      console.log('✅ User logged out and localStorage cleared');
    },

    // Restore authentication from localStorage (on app startup)
    restoreAuth: (state) => {
      console.log('🔄 RESTORE AUTH CALLED');
      
      try {
        const token = localStorage.getItem('crowdtrades_token');
        const userData = localStorage.getItem('crowdtrades_user');
        
        console.log('🔄 Loading from localStorage:');
        console.log('- Token:', token ? '✅ Present' : '❌ Missing');
        console.log('- User data:', userData ? '✅ Present' : '❌ Missing');
        
        if (token && userData) {
          const user = JSON.parse(userData);
          state.isAuthenticated = true;
          state.user = user;
          state.token = token;
          state.joinedEvents = []; // Will be loaded from database
          state.initialized = true;
          
          console.log(`✅ Auth restored from localStorage:`);
          console.log(`- User: ${user.firstName}`);
          console.log('- Joined events: Will be loaded from database');
        } else {
          state.initialized = true;
          console.log('❌ No valid auth data in localStorage');
        }
      } catch (error) {
        console.error('❌ Error restoring auth:', error);
        state.isAuthenticated = false;
        state.user = null;
        state.token = null;
        state.initialized = true;
      }
    },

    // Update user profile
    updateUser: (state, action: PayloadAction<Partial<AuthUser>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
        localStorage.setItem('crowdtrades_user', JSON.stringify(state.user));
        console.log('✅ User profile updated');
      }
    },

    // Clear any errors
    clearError: (state) => {
      state.error = null;
    }
  },
  
  // =============================================================================
  // ASYNC THUNK REDUCERS - DATABASE OPERATIONS
  // =============================================================================
  extraReducers: (builder) => {
    // Join Event
    builder
      .addCase(joinEventAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(joinEventAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { eventId } = action.payload;
        
        // Add to joinedEvents if not already there
        if (!state.joinedEvents.includes(eventId)) {
          state.joinedEvents.push(eventId);
          console.log(`✅ Event ${eventId} added to joinedEvents:`, state.joinedEvents);
        } else {
          console.log(`⚠️ Event ${eventId} already in joinedEvents`);
        }
      })
      .addCase(joinEventAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error('❌ Join event failed:', action.payload);
      });

    // Leave Event  
    builder
      .addCase(leaveEventAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(leaveEventAsync.fulfilled, (state, action) => {
        state.loading = false;
        const { eventId } = action.payload;
        
        // Remove from joinedEvents
        state.joinedEvents = state.joinedEvents.filter(id => id !== eventId);
        console.log(`✅ Event ${eventId} removed from joinedEvents:`, state.joinedEvents);
      })
      .addCase(leaveEventAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error('❌ Leave event failed:', action.payload);
      });

    // Load Joined Events
    builder
      .addCase(loadJoinedEventsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadJoinedEventsAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.joinedEvents = action.payload;
        console.log(`✅ Loaded ${action.payload.length} joined events from database:`, action.payload);
      })
      .addCase(loadJoinedEventsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        console.error('❌ Load joined events failed:', action.payload);
        // Keep empty array on error
        state.joinedEvents = [];
      });
  },
});

// =============================================================================
// ACTIONS AND SELECTORS
// =============================================================================

export const { 
  login, 
  logout, 
  restoreAuth, 
  updateUser, 
  clearError 
} = authSlice.actions;

// Selectors
export const selectAuth = (state: { auth: AuthState }) => state.auth;
export const selectUser = (state: { auth: AuthState }) => state.auth.user;
export const selectIsAuthenticated = (state: { auth: AuthState }) => state.auth.isAuthenticated;
export const selectJoinedEvents = (state: { auth: AuthState }) => state.auth.joinedEvents;
export const selectAuthLoading = (state: { auth: AuthState }) => state.auth.loading;

export default authSlice.reducer;

